import React, { useContext, useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { isEmpty, pathOr } from 'ramda';
import { useRecoilValue } from 'recoil';

import { useErrorQuery } from '@providers/Errors';
import { useMyUser } from '@providers/User';
import { Feature } from '@views/shared/hooks/hasFeature';
import { colors } from '@views/shared/styles';
import { loggedInAtom } from '@views/Login';
import { timeFromHours } from '@utils/DateAndTime';

interface MyOrganizationProps {
  children: React.ReactElement;
}

export interface Translation {
  id: string;
  key: string;
  value: string;
}

interface IntlTranslations {
  [key: string]: string;
}

type contrastType = 'dark' | 'light';

interface IconColorsEnum {
  DARK: contrastType;
  LIGHT: contrastType;
}

interface MyOrganizationContext {
  workingDaysStart:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
    | string;
  workingDaysEnd:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
    | string;
  workingHoursStart: string;
  workingHoursEnd: string;
  meetingRoomHoursStart: string;
  meetingRoomHoursEnd: string;
  enabledFeatures: Feature[];
  translations: IntlTranslations;
  backgroundColor: string;
  bookingRange: number;
  midDayHour: string;
  id: string;
  logo?: string;
  iconColor: contrastType;
  name: string;
  error: any;
  loading: boolean;
  loaded: boolean;
}

interface Data {
  myOrganization: {
    workingDaysStart:
      | 'monday'
      | 'tuesday'
      | 'wednesday'
      | 'thursday'
      | 'friday'
      | 'saturday'
      | 'sunday'
      | string;
    workingDaysEnd:
      | 'monday'
      | 'tuesday'
      | 'wednesday'
      | 'thursday'
      | 'friday'
      | 'saturday'
      | 'sunday'
      | string;
    workingHoursStart: Date;
    workingHoursEnd: Date;
    meetingRoomHoursStart: string;
    meetingRoomHoursEnd: string;
    enabledFeatures: Feature[];
    translations: Translation[];
    backgroundColor: string;
    bookingRange: number;
    midDayHour: number;
    id: string;
    iconColor: contrastType;
    logo: string;
    name: string;
  };
}

const { white } = colors;

const getMyOrganization = gql`
  query getMyOrganization($language: LanguageEnumType) {
    myOrganization(language: $language) {
      backgroundColor
      bookingRange
      id
      iconColor
      logo
      name
      workingDaysStart
      workingDaysEnd
      workingHoursStart
      workingHoursEnd
      meetingRoomHoursStart
      meetingRoomHoursEnd
      enabledFeatures
      midDayHour
      translations(language: $language) {
        id
        key
        value
      }
    }
  }
`;

export const ICON_COLORS: IconColorsEnum = {
  DARK: 'dark',
  LIGHT: 'light',
};

const DEFAULT_CONTEXT_STATE = {
  workingDaysStart: 'monday',
  workingDaysEnd: 'friday',
  workingHoursStart: '00:00',
  workingHoursEnd: '00:00',
  meetingRoomHoursStart: '00:00',
  meetingRoomHoursEnd: '00:00',
  enabledFeatures: [],
  translations: {},
  backgroundColor: white,
  bookingRange: 0,
  midDayHour: '00:00',
  id: '',
  iconColor: ICON_COLORS.DARK,
  logo: undefined,
  name: '',
  error: {},
  loading: false,
  loaded: false,
};

export const MyOrganizationContext = React.createContext<MyOrganizationContext>(
  DEFAULT_CONTEXT_STATE,
);

export const useMyOrganization = () => useContext(MyOrganizationContext);

export function MyOrganizationProvider({ children }: MyOrganizationProps) {
  const loggedIn = useRecoilValue(loggedInAtom);

  const [id, setId] = useState(DEFAULT_CONTEXT_STATE.id);
  const [logo, setLogo] = useState(DEFAULT_CONTEXT_STATE.logo);
  const [name, setName] = useState(DEFAULT_CONTEXT_STATE.name);
  const [enabledFeatures, setEnabledFeatures] = useState<Feature[]>(
    DEFAULT_CONTEXT_STATE.enabledFeatures,
  );
  const [translations, setTranslations] = useState<any>(
    DEFAULT_CONTEXT_STATE.translations,
  );
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_CONTEXT_STATE.backgroundColor,
  );
  const [iconColor, setIconColor] = useState(DEFAULT_CONTEXT_STATE.iconColor);
  const [bookingRange, setBookingRange] = useState(
    DEFAULT_CONTEXT_STATE.bookingRange,
  );
  const [workingDaysStart, setWorkingDaysStart] = useState(
    DEFAULT_CONTEXT_STATE.workingDaysStart,
  );
  const [workingDaysEnd, setWorkingDaysEnd] = useState(
    DEFAULT_CONTEXT_STATE.workingDaysEnd,
  );
  const [workingHoursStart, setWorkingHoursStart] = useState(
    DEFAULT_CONTEXT_STATE.workingHoursStart,
  );
  const [workingHoursEnd, setWorkingHoursEnd] = useState(
    DEFAULT_CONTEXT_STATE.workingHoursEnd,
  );
  const [meetingRoomHoursStart, setMeetingRoomHoursStart] = useState(
    DEFAULT_CONTEXT_STATE.meetingRoomHoursStart,
  );
  const [meetingRoomHoursEnd, setMeetingRoomHoursEnd] = useState(
    DEFAULT_CONTEXT_STATE.meetingRoomHoursEnd,
  );
  const [midDayHour, setMidDayHour] = useState(
    DEFAULT_CONTEXT_STATE.midDayHour,
  );

  const { loaded, loading, language } = useMyUser();

  const {
    data,
    refetch,
    error,
    loading: loadingOrganization,
  } = useErrorQuery<Data>(getMyOrganization, {
    variables: {
      language: language,
    },
    skip: !loggedIn || !loaded || loading,
    finderError: {
      type: 'fatal',
      message: 'TopBar.fetchError',
    },
  });

  useEffect(() => {
    if (data) {
      const id = pathOr('', ['myOrganization', 'id'], data);
      const logo = pathOr(undefined, ['myOrganization', 'logo'], data);
      const name = pathOr('', ['myOrganization', 'name'], data); //TODO: can be an organization without name??
      const myBookingRange = pathOr(
        0,
        ['myOrganization', 'bookingRange'],
        data,
      );
      const myTranslations = pathOr(
        [],
        ['myOrganization', 'translations'],
        data,
      );
      const customTranslations = myTranslations.reduce((acc, val) => {
        const { key, value } = val;

        return { ...acc, [key]: value };
      }, {});

      const myWorkingDaysStart = pathOr(
        'monday',
        ['myOrganization', 'workingDaysStart'],
        data,
      );

      const myWorkingDaysEnd = pathOr(
        'friday',
        ['myOrganization', 'workingDaysEnd'],
        data,
      );

      const myWorkingHoursStart = pathOr(
        '',
        ['myOrganization', 'workingHoursStart'],
        data,
      );

      const myWorkingHoursEnd = pathOr(
        '',
        ['myOrganization', 'workingHoursEnd'],
        data,
      );

      const myIconColor = pathOr(
        ICON_COLORS.DARK,
        ['myOrganization', 'iconColor'],
        data,
      );

      let myBackgroundColor = pathOr(
        white,
        ['myOrganization', 'backgroundColor'],
        data,
      );

      if (!myBackgroundColor.length) {
        myBackgroundColor = white;
      }

      if (!myBackgroundColor?.startsWith('#')) {
        myBackgroundColor = `#${myBackgroundColor}`;
      }

      const myMeetingRoomHoursStart = pathOr(
        '',
        ['myOrganization', 'meetingRoomHoursStart'],
        data,
      );
      const myMeetingRoomHoursEnd = pathOr(
        '',
        ['myOrganization', 'meetingRoomHoursEnd'],
        data,
      );

      const myEnabledFeatures = pathOr(
        [],
        ['myOrganization', 'enabledFeatures'],
        data,
      );

      const myMidDayHour = pathOr(0, ['myOrganization', 'midDayHour'], data);

      setId(id);
      setLogo(logo);
      setIconColor(myIconColor);
      setName(name);
      setTranslations(customTranslations);
      setBackgroundColor(myBackgroundColor);
      setBookingRange(myBookingRange);
      setMeetingRoomHoursStart(myMeetingRoomHoursStart);
      setMeetingRoomHoursEnd(myMeetingRoomHoursEnd);
      setEnabledFeatures(myEnabledFeatures);
      setWorkingHoursStart(myWorkingHoursStart);
      setWorkingHoursEnd(myWorkingHoursEnd);
      setWorkingDaysStart(myWorkingDaysStart);
      setWorkingDaysEnd(myWorkingDaysEnd);
      setMidDayHour(timeFromHours(myMidDayHour));
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch, language]);

  return (
    <MyOrganizationContext.Provider
      value={{
        id,
        logo,
        name,
        backgroundColor,
        iconColor,
        enabledFeatures,
        bookingRange,
        translations,
        meetingRoomHoursStart,
        meetingRoomHoursEnd,
        workingHoursStart,
        workingHoursEnd,
        workingDaysStart,
        workingDaysEnd,
        midDayHour,
        error,
        loading: loadingOrganization,
        loaded: !isEmpty(id),
      }}
    >
      {children}
    </MyOrganizationContext.Provider>
  );
}
